import React, { useState } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import './AddOrganizationModal.css';
import { CustomModal } from '../../../../components';

interface AddOrganizationInterface {
  show: boolean;
  onClose: { (): void };
  onSubmit: {
    (CompanyName: string, EmailAddress: string): void;
  };
}

const AddOrganizationModal: React.FC<AddOrganizationInterface> = ({
  show,
  onClose,
  onSubmit,
}) => {
  const [organizationDetails, setOrganizationDetails] = useState({
    Name: '',
    Email: '',
  });

  const [errors, setErrors] = useState({
    Name: '',
    Email: '',
  });

  const [touched, setTouched] = useState({
    Name: false,
    Email: false,
  });

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'Name':
        if (!value.trim()) {
          return 'Organization name is required';
        }
        if (value.trim().length < 2) {
          return 'Organization name must be at least 2 characters';
        }
        return '';
      case 'Email':
        if (!value.trim()) {
          return 'Email address is required';
        }
        if (!validateEmail(value)) {
          return 'Please enter a valid email address';
        }
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setOrganizationDetails({
      ...organizationDetails,
      [name]: value,
    });

    // Validate on change if field has been touched
    if (touched[name as keyof typeof touched]) {
      setErrors({
        ...errors,
        [name]: validateField(name, value),
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });
    setErrors({
      ...errors,
      [name]: validateField(name, value),
    });
  };

  const isFormValid = (): boolean => {
    const nameValid = organizationDetails.Name.trim().length >= 2;
    const emailValid = validateEmail(organizationDetails.Email);
    return nameValid && emailValid;
  };

  const handleSubmit = () => {
    // Validate all fields before submit
    const nameError = validateField('Name', organizationDetails.Name);
    const emailError = validateField('Email', organizationDetails.Email);

    if (nameError || emailError) {
      setErrors({
        Name: nameError,
        Email: emailError,
      });
      setTouched({
        Name: true,
        Email: true,
      });
      return;
    }

    onSubmit(organizationDetails.Name.trim(), organizationDetails.Email.trim());
  };

  return (
    <CustomModal
      show={show}
      onClose={onClose}
      headerMsg={'Add Organization Details'}
      isSubmitBtnDisabled={!isFormValid()}
      submitFunc={handleSubmit}
    >
      <Box className="AddOrgBody">
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, fontSize: '14px' }}
        >
          Please provide the organization details below. All fields are
          required.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              inputProps={{ style: { fontSize: 16 } }}
              InputLabelProps={{ style: { fontSize: 16 } }}
              id="organization-name"
              label="Organization Name"
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              value={organizationDetails.Name}
              name="Name"
              required
              autoComplete="off"
              error={touched.Name && !!errors.Name}
              helperText={touched.Name && errors.Name}
              placeholder="Enter organization name"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              inputProps={{ style: { fontSize: 16 } }}
              InputLabelProps={{ style: { fontSize: 16 } }}
              id="organization-email"
              label="Email Address"
              variant="outlined"
              onChange={handleChange}
              onBlur={handleBlur}
              value={organizationDetails.Email}
              name="Email"
              type="email"
              required
              autoComplete="off"
              error={touched.Email && !!errors.Email}
              helperText={touched.Email && errors.Email}
              placeholder="organization@example.com"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '13px' }}
          >
            <strong>Note:</strong> The organization will be created with the
            provided details. Make sure the email address is valid as it will be
            used for communications.
          </Typography>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default AddOrganizationModal;
